import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { DealerAudit } from '@cdab/scania/qpr/schema'
import {
  filterTableData,
  sortTableData,
  type SortConfig
} from '@cdab/scania/qpr/utils'
import { capitalizeFirstLetter, formatISODate } from '@cdab/utils'
import {
  TdsBodyCell,
  TdsHeaderCell,
  TdsTable,
  TdsTableBody,
  TdsTableBodyRow,
  TdsTableHeader,
  TdsTableToolbar
} from '@scania/tegel-react'
import type { TFunction } from 'i18next'

export interface AuditsTableProps {
  audits: DealerAudit[]
  onRowClick: (auditId: number) => void
}

type PreparedAuditData = {
  auditDate: string | null
  isCertifyingAudit: string | null
  auditDescription: string
  auditor: string
  extraAuditors: string
  auditType: string
  dosVersion: string | null
  hasDeviations: string
  hasNotes: string
  id: number
}

function prepareTableData(
  data: readonly DealerAudit[],
  t: TFunction
): PreparedAuditData[] {
  const preparedData = data
    .map(dealerAudit => {
      return {
        auditDate: dealerAudit.auditDate
          ? formatISODate(dealerAudit.auditDate)
          : null,
        isCertifyingAudit: dealerAudit.isCertificationAudit ? t('yes') : null,
        auditDescription: dealerAudit.auditDescription,
        auditor: dealerAudit.auditor,
        extraAuditors: dealerAudit.extraAuditors,
        auditType: dealerAudit.auditTypeDescription,
        dosVersion: dealerAudit.dosVersion,
        hasDeviations: dealerAudit.hasDeviations ? t('yes') : t('no'),
        hasNotes: dealerAudit.hasNotes ? t('yes') : t('no'),
        id: dealerAudit.id
      }
    })
    .sort((a, b) => {
      const aDate = a.auditDate || ''
      const bDate = b.auditDate || ''

      return aDate > bDate ? -1 : aDate < bDate ? 1 : 0
    })

  return preparedData
}

export function AuditsTable({ audits, onRowClick }: AuditsTableProps) {
  const { t } = useTranslation('common')
  const [sortConfig, setSortConfig] = useState<SortConfig>(null)
  const [filterQuery, setFilterQuery] = useState('')
  const [processedData, setProcessedData] = useState<PreparedAuditData[]>([])

  const handleTableSort = useCallback(
    (
      event: CustomEvent<{
        tableId: string | undefined
        columnKey: string | undefined
        sortingDirection: 'asc' | 'desc'
      }>
    ) => {
      if (!event.detail.columnKey) {
        setSortConfig(null)
        return
      }
      const { columnKey, sortingDirection } = event.detail
      setSortConfig({ columnKey, sortingDirection })
    },
    []
  )

  const handleTableFilter = useCallback(
    (
      event: CustomEvent<{
        tableId: string | undefined
        query: string | undefined
      }>
    ) => {
      if (!event.detail.query) {
        setFilterQuery('')
        return
      }
      const { query } = event.detail
      setFilterQuery(query)
    },
    []
  )

  const handleRowClick = (auditId: number) => {
    onRowClick(auditId)
  }

  useEffect(() => {
    let data = prepareTableData(audits, t)

    if (filterQuery) {
      data = filterTableData(data, filterQuery)
    }

    if (sortConfig) {
      data = sortTableData(data, sortConfig)
    }

    setProcessedData(data)
  }, [audits, sortConfig, filterQuery, t])

  return (
    <TdsTable compactDesign={true} responsive={true}>
      <TdsTableToolbar
        filter
        tableTitle={capitalizeFirstLetter('audits')}
        onTdsFilter={handleTableFilter}
      />
      <TdsTableHeader>
        <TdsHeaderCell
          onTdsSort={handleTableSort}
          cellKey='auditDate'
          cellValue={t('date')}
          sortable
        />
        <TdsHeaderCell
          onTdsSort={handleTableSort}
          cellKey='isCertifyingAudit'
          cellValue={capitalizeFirstLetter(t('certification'))}
          sortable
        />
        <TdsHeaderCell
          onTdsSort={handleTableSort}
          cellKey='auditDescription'
          cellValue={t('description')}
          sortable
        />
        <TdsHeaderCell
          onTdsSort={handleTableSort}
          cellKey='auditor'
          cellValue={capitalizeFirstLetter(t('auditor'))}
          sortable
        />
        <TdsHeaderCell
          onTdsSort={handleTableSort}
          cellKey='extraAuditors'
          cellValue={t('extra-auditors')}
          sortable
        />
        <TdsHeaderCell
          onTdsSort={handleTableSort}
          cellKey='auditType'
          cellValue={capitalizeFirstLetter(t('audit-type'))}
          sortable
        />
        <TdsHeaderCell
          onTdsSort={handleTableSort}
          cellKey='dosVersion'
          cellValue={t('dos-version')}
          sortable
        />
        <TdsHeaderCell
          onTdsSort={handleTableSort}
          cellKey='hasDeviations'
          cellValue={t('has-deviations')}
          sortable
        />
        <TdsHeaderCell
          onTdsSort={handleTableSort}
          cellKey='hasNotes'
          cellValue={t('has-notes')}
          sortable
        />
      </TdsTableHeader>
      <TdsTableBody>
        {processedData.map((audit, index) => (
          <TdsTableBodyRow
            key={audit.id || index}
            clickable={true}
            onTdsClick={() => handleRowClick(audit.id)}
          >
            <TdsBodyCell
              cellKey='auditDate'
              cellValue={audit.auditDate ?? undefined}
            />
            <TdsBodyCell
              cellKey='isCertifyingAudit'
              cellValue={audit.isCertifyingAudit ?? undefined}
            />
            <TdsBodyCell
              cellKey='auditDescription'
              cellValue={audit.auditDescription}
            />
            <TdsBodyCell cellKey='auditor' cellValue={audit.auditor} />
            <TdsBodyCell
              cellKey='extraAuditors'
              cellValue={audit.extraAuditors}
            />
            <TdsBodyCell cellKey='auditType' cellValue={audit.auditType} />
            <TdsBodyCell
              cellKey='dosVersion'
              cellValue={audit.dosVersion ?? undefined}
            />
            <TdsBodyCell
              cellKey='hasDeviations'
              cellValue={audit.hasDeviations}
            />
            <TdsBodyCell cellKey='hasNotes' cellValue={audit.hasNotes} />
          </TdsTableBodyRow>
        ))}
      </TdsTableBody>
    </TdsTable>
  )
}
